// hooks and Types
import { useNewGroup, FieldErrors, GroupDetails, RoleGroupItem, PermissionGroup, GroupPermissionItem } from "containers/NewGroup/useNewGroup";
import { useRoleManagement } from "containers/RoleManagement/useRoleManagement";

// Components
import PageTitle from "components/molecules/PageTitle/PageTitle";
import RoleGroup from "components/molecules/RoleGroup/RoleGroup";
import Button from "components/atoms/Button/Button";
import NewGroupDetails from "components/organisms/NewGroup/NewGroupDetails";
import { Head } from "components/molecules/Head/Head";
import Spinner from "components/atoms/Spinner/Spinner";
import Label from "components/atoms/Label/Label";
import UnauthorisedAccessError from "components/molecules/ErrorPages/UnauthorisedAccessError";

// Styles
import styles from "containers/NewGroup/newGroupPage.module.css";

// Constants
import { NewGroup as Constants, PageTitles } from "constants/global";

type ComponentProps = {
	groupDetails: GroupDetails;
	isCreateGroupButtonEnabled: boolean;
	selectedRoles?: Array<string>;
	loading: boolean;
	backgroundLoading: boolean;
	permissionList: PermissionGroup[];
	fieldErrors: FieldErrors;
	validateFields: (arg: boolean) => void;
	handleOnPermissionSelect: (value: string) => void;
	handleGoBack: () => void;
	proceedToCreateGroup: () => void;
	handleGroupDetailsChange: ({ id, value }: RoleGroupItem) => void;
};

const NewGroupPage = () => {
	const componentProps: ComponentProps = useNewGroup();
	const { createGroupAccess } = useRoleManagement();

	const {
		loading,
		permissionList,
		selectedRoles,
		groupDetails,
		isCreateGroupButtonEnabled,
		fieldErrors,
		backgroundLoading,
		validateFields,
		handleOnPermissionSelect,
		proceedToCreateGroup,
		handleGoBack,
		handleGroupDetailsChange
	} = componentProps;

	if (loading) {
		return <Spinner />;
	}

	const createGroupBtnType = isCreateGroupButtonEnabled ? "active" : "disabled";

	return (
		<>
			{createGroupAccess ? (
				<div className={styles?.root}>
					{backgroundLoading && <Spinner type="transparent" />}
					<Head>{PageTitles.NEW_GROUP}</Head>
					<PageTitle showBackIcon={true} title={Constants.NEW_GROUP_HEADER} subTitle={Constants.NEW_GROUP_SUBHEADER} goBack={handleGoBack} />
					<div className={styles?.main_wrapper}>
						<div>
							<div className={styles?.step_header_wrapper}>
								<h1 className={styles?.step_header}>{Constants.STEP_1_TEXT}</h1>
								<h2 className={styles?.step_subheader}>{Constants.STEP_1_SUBHEADER}</h2>
							</div>
							<div className={styles?.content}>
								<NewGroupDetails validateFields={validateFields} fieldErrors={fieldErrors} groupDetails={groupDetails} handleGroupDetailsChange={handleGroupDetailsChange} />
							</div>
						</div>
						<div className={styles?.admin_rights_group_wrapper}>
							<div className={styles?.step_header_wrapper}>
								<h1 className={styles?.step_header}>{Constants.STEP_2_TEXT}</h1>
								<h2 className={styles?.step_subheader}>
									<Label label={Constants.STEP_2_SUBHEADER} requiredMark />
								</h2>
							</div>
							<div className={styles?.rights_group}>
								{permissionList?.map(({ title, values, disable }: { title?: string; values?: Array<GroupPermissionItem>; disable?: boolean }, idx: number) => {
									return <RoleGroup disabled={disable} checkedRoles={selectedRoles} onClick={handleOnPermissionSelect} title={title} values={values} key={`${title}-${idx}`} />;
								})}
							</div>
						</div>
						<div className={styles?.create_group_wrapper}>
							<Button onClick={proceedToCreateGroup} type={createGroupBtnType} label={Constants.CREATE_GROUP_TEXT} />
						</div>
					</div>
				</div>
			) : (
				<UnauthorisedAccessError />
			)}
		</>
	);
};

export default NewGroupPage;
